import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Format an ISO timestamp for auto-schedule confirmation toasts.
function _fmtWhen(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleString(undefined, {
            weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });
    } catch {
        return String(iso).slice(0, 16).replace("T", " at ");
    }
}

// Any response carrying `auto_scheduled` (list of notices) pops a confirmation so the
// provider/nurse/facility staff sees the booked date and knows no manual appointment is
// needed — or a warning to intervene when the auto-booking needs attention.
function _emitAutoScheduleToasts(res) {
    try {
        const list = res?.data?.auto_scheduled;
        if (!Array.isArray(list) || !list.length) return;
        for (const n of list) {
            if (!n) continue;
            const when = _fmtWhen(n.scheduled_at);
            const label = n.context || (n.appointment_type || "").replace(/_/g, " ") || "Appointment";
            if (n.status === "scheduled") {
                let desc = `${label} with ${n.provider_name || "a provider"}${when ? ` — ${when}` : ""}. No manual booking needed — intervene only if this needs to be sooner.`;
                if (n.mat_provider_fallback) {
                    desc += " ⚠ No MAT provider was available — booked with a non-MAT provider; designate a MAT provider.";
                    toast.warning("Appointment auto-scheduled", { description: desc, duration: 11000 });
                } else {
                    toast.success("Appointment auto-scheduled", { description: desc, duration: 9000 });
                }
            } else if (n.status === "already_exists") {
                toast.info("No new appointment needed", {
                    description: n.message || `${label}: patient already has an open appointment.`,
                    duration: 8000,
                });
            } else {
                toast.warning("Auto-scheduling needs attention", {
                    description: n.message || `${label} could not be auto-booked — please schedule this manually.`,
                    duration: 12000,
                });
            }
        }
    } catch { /* no-op */ }
}

// A gated action (security round, med pass, infirmary/psych note, intake screen) that
// went through anyway on an area the staff member isn't assigned to today — always a
// soft warning, never a block (see staff_assignments_service.check_action_area).
function _emitAssignmentWarning(res) {
    try {
        const msg = res?.data?.assignment_warning;
        if (msg) toast.warning("Outside your assignment", { description: msg, duration: 9000 });
    } catch { /* no-op */ }
}

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

// Inject Authorization header if present
api.interceptors.request.use((config) => {
    const token = localStorage.getItem("bp_access_token");
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// On a 401 (expired access token), transparently slide the session forward via
// the httpOnly refresh-token cookie and retry the original request once. This
// keeps ACTIVE inmates alive despite their short 15-min token; if the refresh
// itself fails (truly expired session), clear the token and bounce to landing.
let _refreshing = null;
api.interceptors.response.use(
    (res) => {
        // Instant crisis-bell refresh: any successful mutation may resolve a tracked
        // clock (MH-notify, SIR file, MOUD offer, screen/IHA completion, encounter sign…),
        // so ping the banner to reload rather than waiting for its 60s poll.
        try {
            const m = (res?.config?.method || "").toLowerCase();
            const url = res?.config?.url || "";
            if (["post", "put", "patch", "delete"].includes(m) && !url.includes("/auth/")
                && typeof window !== "undefined") {
                window.dispatchEvent(new Event("bp:refresh-crisis-bell"));
            }
        } catch { /* no-op */ }
        _emitAutoScheduleToasts(res);
        _emitAssignmentWarning(res);
        return res;
    },
    async (error) => {
        const original = error.config || {};
        const status = error.response?.status;
        const url = original.url || "";
        const isAuthRoute = url.includes("/auth/login") || url.includes("/auth/claim-access") || url.includes("/auth/refresh") || url.includes("/auth/me");
        if (status === 401 && !original._retry && !isAuthRoute) {
            original._retry = true;
            try {
                if (!_refreshing) _refreshing = api.post("/auth/refresh");
                const { data } = await _refreshing;
                _refreshing = null;
                if (data?.access_token) setAccessToken(data.access_token);
                original.headers = original.headers || {};
                original.headers.Authorization = `Bearer ${getAccessToken()}`;
                return api(original);
            } catch (e) {
                _refreshing = null;
                setAccessToken(null);
                if (typeof window !== "undefined" && !window.location.pathname.startsWith("/claim") && window.location.pathname !== "/") {
                    window.location.href = "/";
                }
                return Promise.reject(e);
            }
        }
        return Promise.reject(error);
    }
);

export function setAccessToken(token) {
    if (token) localStorage.setItem("bp_access_token", token);
    else localStorage.removeItem("bp_access_token");
}

export function getAccessToken() {
    return localStorage.getItem("bp_access_token");
}

// Fetch a PDF (auth headers included) and trigger a browser download
export async function downloadPdf(path, filename) {
    const resp = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

// POST a body and download the PDF response (with auth header).
export async function downloadPdfPost(path, body, filename) {
    const resp = await api.post(path, body, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}


// Fetch a file (with the auth header) and open it inline in a new tab for viewing.
export async function openFile(path) {
    const viewerPath = `/file-viewer?path=${encodeURIComponent(path)}`;
    const win = window.open(viewerPath, "_blank");
    if (win) win.opener = null;
    if (!win) {
        const resp = await api.get(path, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
}
